/**
 * Сила привычки, стрик и доля выполнения.
 *
 * Файл намеренно не знает ни о базе, ни о React: это чистые функции над
 * массивом значений по дням. Так расчёт можно покрыть тестами без IndexedDB и
 * переиспользовать в фоновых задачах (например, в будущем виджете на рабочем
 * столе).
 *
 * ## Почему именно формула Loop Habit Tracker
 *
 * Наивный «процент выполнения за месяц» одинаково оценивает того, кто три
 * недели держался и сорвался вчера, и того, кто сорвался в первую неделю и с
 * тех пор не пропускает. Экспоненциальное сглаживание из Loop (`Score.kt`)
 * решает это: свежие дни весят больше, а прошлое забывается с постоянной
 * скоростью.
 *
 * ```
 * multiplier = 0.5 ^ (sqrt(frequency) / 13)
 * score      = prevScore * multiplier + checkmarkValue * (1 - multiplier)
 * ```
 *
 * Число 13 — период полураспада в днях: за 13 дней полного бездействия сила
 * привычки падает вдвое. Для ежедневной привычки multiplier ≈ 0,948, поэтому
 * непрерывное выполнение даёт ~80% силы за месяц, ~96% за два и ~99% за три.
 * Корень из частоты выравнивает шкалу для редких привычек: «три раза в неделю»
 * не должно расти втрое медленнее ежедневной.
 */

import { HABIT_VALUE, type Habit, type HabitDirection, type HabitType } from '@/shared/db/schema';
import { countDays, hasWeekday } from '@/shared/lib/weekdays';
import { dateRange, diffDays, type IsoDate, weekdayIndex } from '@/shared/lib/datetime';

/** Период полураспада силы привычки в днях (константа Loop). */
export const SCORE_HALF_LIFE_DAYS = 13;

/* ────────────────────────── Частота и расписание ────────────────────────── */

/** Частота как дробь «numerator раз за denominator дней». */
export interface HabitFrequency {
  numerator: number;
  denominator: number;
}

type ScheduleFields = Pick<
  Habit,
  'scheduleType' | 'weekdaysMask' | 'freqNumerator' | 'freqDenominator' | 'intervalDays'
>;



/**
 * Приводит любое расписание к частоте «сколько раз за сколько дней».
 * Это единственная величина, которая нужна формуле силы, поэтому все четыре
 * типа расписания сводятся к ней и дальше обрабатываются одинаково.
 */
export function habitFrequency(habit: ScheduleFields): HabitFrequency {
  switch (habit.scheduleType) {
    case 'daily':
      return { numerator: 1, denominator: 1 };
    case 'weekdays': {
      // Маска без единого дня означала бы «никогда» — трактуем как ежедневно,
      // иначе частота 0 обнулила бы множитель и сила навсегда осталась бы нулём.
      const days = countDays(habit.weekdaysMask);
      return days > 0 ? { numerator: days, denominator: 7 } : { numerator: 1, denominator: 1 };
    }
    case 'timesPerWeek':
      return {
        numerator: Math.max(1, habit.freqNumerator),
        denominator: Math.max(1, habit.freqDenominator),
      };
    case 'everyNDays':
      return { numerator: 1, denominator: Math.max(1, habit.intervalDays) };
  }
}

/**
 * Запланирована ли привычка на этот день.
 *
 * Для `timesPerWeek` ответ всегда «да»: пользователь сам выбирает, в какие дни
 * из недели выполнить норму, и любой день подходит.
 */
export function isScheduledOn(
  habit: ScheduleFields & Pick<Habit, 'startDate'>,
  date: IsoDate,
): boolean {
  if (date < habit.startDate) return false;

  switch (habit.scheduleType) {
    case 'daily':
    case 'timesPerWeek':
      return true;
    case 'weekdays':
      return hasWeekday(habit.weekdaysMask, weekdayIndex(date));
    case 'everyNDays': {
      const interval = Math.max(1, habit.intervalDays);
      return diffDays(habit.startDate, date) % interval === 0;
    }
  }
}

/* ────────────────────────────── Спецификация ────────────────────────────── */

/**
 * Всё, что нужно расчёту от привычки. Отдельный тип, чтобы функции можно было
 * вызывать в тестах, не собирая полную запись `Habit` со служебными полями.
 */
export interface ScoreSpec {
  type: HabitType;
  direction: HabitDirection;
  /** Дневная цель в единицах хранения: numeric ×1000, duration — в секундах. */
  targetValue: number;
  /** Числитель окна после возможного удвоения (см. `scoreSpecOf`). */
  numerator: number;
  /** Длина скользящего окна в днях после возможного удвоения. */
  denominator: number;
  /** Исходная частота `numerator / denominator` — удвоение её не меняет. */
  frequency: number;
}

export function scoreSpecOf(
  habit: ScheduleFields & Pick<Habit, 'type' | 'direction' | 'targetValue'>,
): ScoreSpec {
  const { numerator, denominator } = habitFrequency(habit);
  const frequency = numerator / denominator;

  /*
   * Для редких привычек (реже раза в день) Loop удваивает числитель и
   * знаменатель. Частота при этом не меняется, но окно становится шире: три
   * пробежки за неделю можно распределить как «две и одна» через границу
   * недели, и оценка не провалится из-за неудачно легшего календаря.
   */
  const double = habit.type === 'binary' && frequency < 1;

  return {
    type: habit.type,
    direction: habit.direction,
    targetValue: habit.targetValue,
    numerator: double ? numerator * 2 : numerator,
    denominator: double ? denominator * 2 : denominator,
    frequency,
  };
}

/* ───────────────────────────────── Формула ───────────────────────────────── */

/** Коэффициент «забывания» за один день при заданной частоте. */
export function scoreMultiplier(frequency: number): number {
  return 0.5 ** (Math.sqrt(Math.max(0, frequency)) / SCORE_HALF_LIFE_DAYS);
}

/** Один шаг экспоненциального сглаживания. `checkmarkValue` — доля 0…1. */
export function nextScore(
  previousScore: number,
  checkmarkValue: number,
  frequency: number,
): number {
  const multiplier = scoreMultiplier(frequency);
  return previousScore * multiplier + checkmarkValue * (1 - multiplier);
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/** Отметка «день намеренно пропущен» — исключается из всех расчётов. */
export function isSkipped(value: number): boolean {
  // Для numeric значения хранятся ×1000, поэтому совпадение реального замера
  // с сентинелом 3 (то есть 0,003 единицы) практически невозможно.
  return value === HABIT_VALUE.SKIP;
}

export function isYes(value: number): boolean {
  return value === HABIT_VALUE.YES_MANUAL || value === HABIT_VALUE.YES_AUTO;
}

/**
 * Вклад одного дня в скользящую сумму, нормированный к дневной цели.
 * Для binary это 0 или 1, для numeric/duration — доля выполнения (может быть
 * больше единицы: перевыполнение компенсирует соседний слабый день).
 */
function dailyAchievement(spec: ScoreSpec, value: number): number {
  if (value === HABIT_VALUE.UNKNOWN) return 0;
  if (spec.type === 'binary') return isYes(value) ? 1 : 0;
  if (spec.targetValue <= 0) return value > 0 ? 1 : 0;
  return Math.max(0, value) / spec.targetValue;
}

/**
 * Значение дня для вредной привычки: чем сильнее перебор относительно
 * допустимого, тем ниже оценка. Формула из RESEARCH.md.
 */
function atMostValue(sum: number, allowance: number): number {
  // Нулевой лимит («ни одной сигареты») — деление невозможно, решаем логически.
  if (allowance <= 0) return sum <= 0 ? 1 : 0;
  return clamp01(1 - (sum - allowance) / allowance);
}

/**
 * Сила привычки по дням. `values` — значения в хронологическом порядке;
 * пропущенные дни должны быть заполнены `HABIT_VALUE.UNKNOWN`.
 *
 * Возвращает массив той же длины: элемент i — сила после дня i. Это нужно для
 * графика динамики, а текущая сила — последний элемент.
 */
export function computeScoreSeries(spec: ScoreSpec, values: readonly number[]): number[] {
  const isAtMost = spec.direction === 'atMost';
  // Вредная привычка стартует с максимума: пока её не совершают, всё хорошо.
  let score = isAtMost ? 1 : 0;

  const window: number[] = [];
  let rollingSum = 0;
  const series: number[] = [];

  for (const value of values) {
    if (isSkipped(value)) {
      // Осознанный пропуск не наказывается и не поощряется: день выпадает из
      // расчёта целиком, окно не сдвигается.
      series.push(score);
      continue;
    }

    const achievement = dailyAchievement(spec, value);
    window.push(achievement);
    rollingSum += achievement;
    if (window.length > spec.denominator) rollingSum -= window.shift() ?? 0;

    const checkmark = isAtMost
      ? atMostValue(rollingSum, spec.type === 'binary' ? 0 : spec.numerator)
      : clamp01(spec.numerator > 0 ? rollingSum / spec.numerator : rollingSum);

    score = nextScore(score, checkmark, spec.frequency);
    series.push(score);
  }

  return series;
}

/** Текущая сила привычки, 0…1. Пустая история даёт 0 (для `atMost` — 1). */
export function computeScore(spec: ScoreSpec, values: readonly number[]): number {
  const series = computeScoreSeries(spec, values);
  return series[series.length - 1] ?? (spec.direction === 'atMost' ? 1 : 0);
}

/* ─────────────────────────────────  Стрик  ───────────────────────────────── */

export interface StreakSummary {
  current: number;
  best: number;
}

/**
 * Текущая и лучшая серии.
 *
 * Правило из RESEARCH.md: серию продолжает любое значение `> 0`. Поскольку
 * `YES_AUTO` (незапланированный день) и `SKIP` тоже больше нуля, ни плановые
 * выходные, ни отпуск цепочку не рвут — рвёт только `NO` или незакрытый
 * запланированный день.
 *
 * Последний день, если он ещё не отмечен, из текущей серии исключается:
 * сегодня решение просто не принято, и показывать «стрик обнулился» в полдень
 * было бы неверно.
 */
export function computeStreaks(values: readonly number[]): StreakSummary {
  let best = 0;
  let run = 0;

  for (const value of values) {
    if (value > 0) {
      run += 1;
      if (run > best) best = run;
    } else {
      run = 0;
    }
  }

  let index = values.length - 1;
  if (index >= 0 && values[index] === HABIT_VALUE.UNKNOWN) index -= 1;

  let current = 0;
  for (; index >= 0; index -= 1) {
    if ((values[index] ?? 0) > 0) current += 1;
    else break;
  }

  return { current, best };
}

/* ──────────────────────────── Доля выполнения ──────────────────────────── */

/** Считается ли день выполненным (для тепловой карты и статистики). */
export function isDayCompleted(spec: ScoreSpec, value: number): boolean {
  if (value === HABIT_VALUE.UNKNOWN || isSkipped(value)) return false;

  if (spec.type === 'binary') {
    return spec.direction === 'atMost' ? !isYes(value) : isYes(value);
  }
  if (spec.targetValue <= 0) return value > 0;
  return spec.direction === 'atMost' ? value <= spec.targetValue : value >= spec.targetValue;
}

/** День с частичным результатом: начат, но до цели не дотянул. */
export function dayProgress(spec: ScoreSpec, value: number): number {
  if (value === HABIT_VALUE.UNKNOWN || isSkipped(value)) return 0;
  if (spec.type === 'binary') return isYes(value) ? 1 : 0;
  if (spec.targetValue <= 0) return value > 0 ? 1 : 0;
  return clamp01(Math.max(0, value) / spec.targetValue);
}

/**
 * Выполнение за период дробью: «сделано из ожидаемого».
 *
 * Это база, из которой считается процент. Она существует отдельно и считается
 * здесь по одной причине: процент без базы человек прочитать не может. «82 %»
 * не говорит ни сколько дней сделано, ни сколько ожидалось, — а «24 из 30»
 * говорит и то и другое, и никакого обучения не требует.
 *
 * Знаменатель — не число дней, а ожидаемое число выполнений с учётом частоты.
 * Иначе привычка «три раза в неделю» никогда не поднялась бы выше 43%, и
 * сравнивать её с ежедневной было бы бессмысленно. Дни со `SKIP` не учитываются.
 */
export interface CompletionCount {
  /** Сколько дней засчитано выполненными. */
  done: number;
  /** Сколько выполнений ожидалось за тот же период. */
  expected: number;
}

export function completionCount(spec: ScoreSpec, values: readonly number[]): CompletionCount {
  const counted = values.filter((value) => !isSkipped(value));
  if (counted.length === 0) return { done: 0, expected: 0 };

  return {
    done: counted.filter((value) => isDayCompleted(spec, value)).length,
    // Округление нужно, чтобы дробь можно было показать человеку: «3 из 12,86»
    // не число, а недоразумение.
    expected: Math.round(counted.length * spec.frequency),
  };
}

/**
 * Доля выполнения за период, 0…1.
 * Считается из той же дроби, что показывается рядом, — иначе процент и его
 * база разошлись бы на округлении.
 */
export function completionRate(spec: ScoreSpec, values: readonly number[]): number {
  const { done, expected } = completionCount(spec, values);
  return expected > 0 ? clamp01(done / expected) : 0;
}

/* ──────────────────────── Сборка истории по дням ──────────────────────── */

export interface HabitDay {
  date: IsoDate;
  /** Сентинел `HABIT_VALUE` либо фактическое значение. */
  value: number;
  /** Запланирована ли привычка на этот день по расписанию. */
  scheduled: boolean;
}

/**
 * Разворачивает отметки из базы в сплошной ряд дней.
 *
 * Отметок в базе только столько, сколько сделал пользователь; расчёту же нужен
 * непрерывный ряд, где отсутствие отметки — это `UNKNOWN`, а не «нет данных».
 *
 * Ряд никогда не начинается раньше `startDate`, даже если запрошенное окно
 * шире возраста привычки. День, в который привычки ещё не существовало, — не
 * пропущенный день: выполнить его было нельзя, и включать его в расчёт значит
 * наказывать человека за время до решения. Без этого отсечения привычка,
 * созданная сегодня, показывала «За 30 дн. 3%» вместо честных 100%, набирала
 * серию в 90 дней из пустоты (незапланированные дни считаются `YES_AUTO`)
 * и рисовала на тепловой карте единственную отметку у правого края.
 */
export function buildHabitDays(
  habit: ScheduleFields & Pick<Habit, 'startDate'>,
  entries: ReadonlyMap<string, number>,
  from: IsoDate,
  to: IsoDate,
): HabitDay[] {
  // Формат `YYYY-MM-DD` сравним лексикографически, поэтому «позднее из двух»
  // берётся обычным сравнением строк, без разбора дат.
  const start = from > habit.startDate ? from : habit.startDate;

  // Привычка со стартом в будущем даёт пустой ряд: `dateRange` при `start > to`
  // возвращает пустой массив, и статистика честно получается нулевой.
  return dateRange(start, to).map((date) => ({
    date,
    value: entries.get(date) ?? HABIT_VALUE.UNKNOWN,
    scheduled: isScheduledOn(habit, date),
  }));
}

/**
 * Продвижение к сроку закрепления привычки.
 *
 * Считаются выполненные дни, а не подряд идущие. Пропуск сбивает серию, но не
 * отменяет уже сделанного: иначе один больничный обнулял бы три недели работы,
 * а это отбивает желание продолжать вернее всего.
 */
export interface HabitTargetProgress {
  /** Сколько дней выполнено. */
  doneDays: number;
  /** Сколько нужно всего. */
  targetDays: number;
  /** Доля 0…1. */
  value: number;
  /** Срок пройден: привычка считается освоенной. */
  isComplete: boolean;
  /**
   * Первый выполненный день — начало попытки.
   *
   * Отличается от даты создания привычки, и разница важна: привычку заводят
   * заранее, а начинают когда получится. Карта отсчитывается отсюда, иначе
   * первая отметка попадала бы в конец ряда пустых дней.
   */
  firstDoneDate: IsoDate | null;
}

export function habitTargetProgress(
  doneDays: number,
  targetDays: number,
  firstDoneDate: IsoDate | null = null,
): HabitTargetProgress {
  const target = Math.max(1, Math.round(targetDays));
  const done = Math.max(0, doneDays);

  return {
    doneDays: done,
    targetDays: target,
    value: Math.min(1, done / target),
    isComplete: done >= target,
    firstDoneDate,
  };
}

/** Пустое продвижение — пока данные ещё не загружены. */
export const NO_TARGET_PROGRESS: HabitTargetProgress = {
  doneDays: 0,
  targetDays: 1,
  value: 0,
  isComplete: false,
  firstDoneDate: null,
};

/* ──────────────────── Свёртка карты по неделям ──────────────────── */

/** Неделя карты: доля выполнения и подпись для подсказки. */
export interface HabitWeek {
  /** Понедельник недели — служит ключом. */
  date: IsoDate;
  /** Доля выполнения, 0…1. */
  progress: number;
  /** Сколько дней недели было запланировано. */
  scheduled: number;
  /** Сколько из них выполнено. */
  done: number;
}

/**
 * Сворачивает дни в недели.
 *
 * Нужно для длинных сроков: год — это 365 клеток, а на телефоне помещается
 * около полутора сотен. Мельчить дальше нельзя (клетка меньше 9 px читается
 * как шум), прокручивать карту — значит прятать большую её часть. Неделя как
 * единица сохраняет и картину целиком, и смысл «квадрат = отрезок времени»:
 * год превращается в 52 клетки, которые видны сразу.
 */
export function foldWeeks(spec: ScoreSpec, days: readonly HabitDay[]): HabitWeek[] {
  const weeks: HabitWeek[] = [];
  let current: HabitWeek | null = null;

  for (const day of days) {
    // Понедельник открывает новую неделю; первая неделя может быть неполной.
    if (current === null || weekdayIndex(day.date) === 0) {
      current = { date: day.date, progress: 0, scheduled: 0, done: 0 };
      weeks.push(current);
    }

    if (isSkipped(day.value)) continue;
    if (day.scheduled) current.scheduled += 1;
    if (isDayCompleted(spec, day.value)) current.done += 1;
  }

  for (const week of weeks) {
    week.progress = week.scheduled > 0 ? Math.min(1, week.done / week.scheduled) : 0;
  }

  return weeks;
}

export interface HabitStats {
  /** Сила привычки, 0…1. */
  score: number;
  currentStreak: number;
  bestStreak: number;
  rate7: number;
  rate30: number;
  rate90: number;
  /** База долей за 30 и 90 дней: процент показывается только вместе с ней. */
  count30: CompletionCount;
  count90: CompletionCount;
}

/**
 * Полная статистика по ряду дней.
 *
 * Две величины считаются по-разному, и это осознанно:
 *  - сила и доля выполнения — по сырым значениям календарных дней (частота уже
 *    заложена в знаменатель, пустые дни между пробежками — часть модели);
 *  - стрик — по значениям, где незапланированные дни заменены на `YES_AUTO`,
 *    иначе выходные рвали бы цепочку у привычки «по будням».
 */
export function computeHabitStats(spec: ScoreSpec, days: readonly HabitDay[]): HabitStats {
  const rawValues = days.map((day) => day.value);
  const streakValues = days.map((day) => (day.scheduled ? day.value : HABIT_VALUE.YES_AUTO));
  const streaks = computeStreaks(streakValues);

  const tail = (count: number): number[] => rawValues.slice(-count);

  const count30 = completionCount(spec, tail(30));
  const count90 = completionCount(spec, tail(90));

  return {
    score: computeScore(spec, rawValues),
    currentStreak: streaks.current,
    bestStreak: streaks.best,
    rate7: completionRate(spec, tail(7)),
    rate30: count30.expected > 0 ? clamp01(count30.done / count30.expected) : 0,
    rate90: count90.expected > 0 ? clamp01(count90.done / count90.expected) : 0,
    count30,
    count90,
  };
}
